import { Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { FormFeedback } from '@/components/ui/form-feedback';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { apiClient } from '@/lib/api-client';
import type { paths } from '@/lib/api-types-clinical-study';
import { formatDate } from '@/lib/intl';
import { usePendingWrite } from '@/lib/use-pending-write';
import { StudyCalendarAgenda } from '../calendar/StudyCalendarAgenda';
import { ContactLogSection } from './ContactLogSection';
import { ScheduleSection } from './ScheduleSection';
import { TreatmentDateRescheduleSection } from './TreatmentDateRescheduleSection';
import { TreatmentDateReservationSection } from './TreatmentDateReservationSection';
import { TreatmentExecutionConfirmationSection } from './TreatmentExecutionConfirmationSection';
import type { StudySubjectListSource } from './useStudySubjectList';
import { formatRomeDate, useReservedTreatmentDate } from './useReservedTreatmentDate';

type StudySubjectSnapshot =
  paths['/v1/clinical-study/studies/{studyId}/subjects']['post']['responses'][201]['content']['application/json'];
type InternalSchedule =
  paths['/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/schedule']['get']['responses'][200]['content']['application/json'];
type Visit =
  paths['/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/visits/{visitId}/appointment']['post']['responses'][200]['content']['application/json'];

type ScheduleState =
  { status: 'loading' } | { status: 'error' } | { status: 'ready'; schedule: InternalSchedule };

// Resolution R11: a literal `as const` map, never a template-string `t()` key.
const LIFECYCLE_LABEL_KEY = {
  PROVISIONAL: 'subjects.lifecyclePROVISIONAL',
  ENROLLED: 'subjects.lifecycleENROLLED',
  CANCELLED: 'subjects.lifecycleCANCELLED',
} as const;

/**
 * The per-Study-Subject action root (Resolution R6): fetches `GET …/subjects/{subjectId}/
 * schedule` on mount (`refreshSchedule()`, `showLoading: true` by default — the ONLY case that
 * renders the top-level loading/error branches below) and again, in the BACKGROUND, after any
 * subject-/visit-level mutation or reschedule conflict (`refreshSchedule({ showLoading: false })`
 * — swaps `schedule` in place on success, silently no-ops on a transient background failure,
 * NEVER flips the panel back to `'loading'`), so every child section's own inline/persistent
 * error state survives a post-action refresh (fixed in the 2026-07-27 draft review — see
 * Divergences). Holds two independent state slices — `schedule` (authoritative for
 * `lifecycleState`/derived visits) and `subjectSnapshot` (session-only, from subject-level write
 * responses only). Renders the phase-appropriate child sections; further children are composed
 * in later activities.
 *
 * Post-execution hardening (2026-07-27, WCAG 2.2 SC 4.1.3, Finding 4): `dropApiError` (this
 * component's own write-outcome banner) renders through `FormFeedback` (`role="alert"`) instead of
 * a plain `<p>`. The top-level `'loading'` branch below stays plain text (no async outcome to
 * announce yet); the top-level `'error'` branch — a full-panel replacement, not a banner alongside
 * a form — also renders through `FormFeedback` (`role="alert"`, closing the residual LOW finding
 * this StepLedger's re-verification pass surfaced), so it stays a full-width text replacement while
 * gaining the announcement role (see this StepLedger's Divergences and notes for the codebase-wide
 * sweep this scoped fix does not attempt).
 */
export function SubjectWorkspacePanel({
  studyId,
  subjectId,
  initialSnapshot,
  reservedTreatmentDatePoolEntryId = null,
  onSubjectWritten,
  subjectList,
  canReadSchedules,
  canReadPool,
}: {
  studyId: string;
  subjectId: string;
  initialSnapshot: StudySubjectSnapshot | null;
  /** From the subject list item, when the read DTO carries it; a write snapshot wins. */
  reservedTreatmentDatePoolEntryId?: string | null;
  /** Called after every successful subject-level write (reserve, confirm, reschedule, drop), so
   * the host can reload the shared subject list the study calendar reads. */
  onSubjectWritten?: () => void;
  /** The page's subject list and permission flags, handed to the embedded agenda (#893). */
  subjectList: StudySubjectListSource;
  canReadSchedules: boolean;
  canReadPool: boolean;
}) {
  const { t } = useTranslation('clinical-study');
  const [scheduleState, setScheduleState] = useState<ScheduleState>({ status: 'loading' });
  const [subjectSnapshot, setSubjectSnapshot] = useState<StudySubjectSnapshot | null>(
    initialSnapshot,
  );
  const [dropApiError, setDropApiError] = useState<string | null>(null);
  const { pending, run } = usePendingWrite();
  // After any subject- or visit-level write the host reloads the shared subject list; each
  // completed list read re-reads the agenda (and the study calendar) in place, never remounting it.
  const afterSubjectWrite = () => {
    onSubjectWritten?.();
  };
  // A CANCELLED subject has no reservation: the backend keeps the entry id as history on drop.
  const cancelled =
    subjectSnapshot?.lifecycleState === 'CANCELLED' ||
    (scheduleState.status === 'ready' && scheduleState.schedule.lifecycleState === 'CANCELLED');
  const reservedEntryId = cancelled
    ? null
    : subjectSnapshot !== null
      ? subjectSnapshot.actualTreatmentDate === null
        ? (subjectSnapshot.reservedTreatmentDatePoolEntryId ?? reservedTreatmentDatePoolEntryId)
        : null
      : reservedTreatmentDatePoolEntryId;
  const reservedDate = useReservedTreatmentDate(reservedEntryId);

  const refreshSchedule = (options: { showLoading?: boolean } = {}) => {
    const showLoading = options.showLoading ?? true;
    if (showLoading) {
      setScheduleState({ status: 'loading' });
    }
    apiClient
      .GET('/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/schedule', {
        params: { path: { studyId, subjectId } },
      })
      .then(({ data, error, response }) => {
        if (error !== undefined || !response.ok || data === undefined) {
          // A background refresh (`showLoading: false`) leaves the currently-rendered `'ready'`
          // schedule untouched on failure — a transient refresh error must never blank out an
          // otherwise-valid panel; only the initial `showLoading: true` load surfaces an error.
          if (showLoading) {
            setScheduleState({ status: 'error' });
          }
          return;
        }
        setScheduleState({ status: 'ready', schedule: data });
      })
      .catch(() => {
        if (showLoading) {
          setScheduleState({ status: 'error' });
        }
      });
  };

  // refreshSchedule is intentionally re-created each render; only studyId/subjectId identity
  // should re-trigger the fetch (this workspace has no react-hooks/exhaustive-deps lint rule
  // configured — mirrors TenantsPage.tsx's own `useEffect(load, [active])` precedent).
  useEffect(refreshSchedule, [studyId, subjectId]);

  // A visit-level write (appointment agree/move/cancel, visit complete/miss/cancel) also changes
  // what both calendars show, so it refreshes them like a subject-level write.
  const handleVisitUpdated = (visit: Visit) => {
    afterSubjectWrite();
    setScheduleState((previous) => {
      if (previous.status !== 'ready') {
        return previous;
      }
      return {
        status: 'ready',
        schedule: {
          ...previous.schedule,
          timepoints: previous.schedule.timepoints.map((row) =>
            row.visitId === visit.id
              ? {
                  ...row,
                  lifecycleState: visit.lifecycleState,
                  appointmentDate: visit.appointmentDate,
                  outOfWindowIndicator: visit.outOfWindowIndicator,
                  hasActiveAppointment: visit.lifecycleState === 'SCHEDULED',
                }
              : row,
          ),
        },
      };
    });
  };

  const onDrop = async () => {
    if (!window.confirm(t('subjects.dropConfirm'))) {
      return;
    }
    setDropApiError(null);
    const { data, error, response } = await apiClient.POST(
      '/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/drop',
      { params: { path: { studyId, subjectId } } },
    );
    if (error !== undefined || !response.ok || data === undefined) {
      setDropApiError(error?.detail ?? t('subjects.genericError'));
      return;
    }
    setSubjectSnapshot(data);
    refreshSchedule({ showLoading: false });
    afterSubjectWrite();
    toast.success(t('subjects.droppedToast'));
  };

  if (scheduleState.status === 'loading') {
    return (
      <SkeletonRegion className="space-y-4">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-4 w-72" />
        <Skeleton className="h-28" />
      </SkeletonRegion>
    );
  }
  if (scheduleState.status === 'error') {
    return <FormFeedback tone="error">{t('subjects.loadError')}</FormFeedback>;
  }

  const { schedule } = scheduleState;

  return (
    <div className="border-border space-y-4 border-t pt-4">
      <div className="space-y-1 text-sm">
        <p>
          <code>{subjectId}</code>
        </p>
        <p>
          {t('subjects.lifecycleLabel')}: {t(LIFECYCLE_LABEL_KEY[schedule.lifecycleState])}
        </p>
        {schedule.actualTreatmentDate !== null && (
          <p>
            {t('subjects.actualTreatmentDateLabel')}: {formatDate(schedule.actualTreatmentDate)}
          </p>
        )}
        {schedule.actualTreatmentDate === null &&
          !cancelled &&
          reservedDate.status !== 'idle' &&
          reservedDate.status !== 'forbidden' && (
            <p>
              {t('subjects.reservedTreatmentDateLabel')}: {reservedDate.status === 'loading' && '…'}
              {reservedDate.status === 'ready' && formatRomeDate(reservedDate.date)}
              {reservedDate.status === 'error' && (
                <>
                  {t('subjects.reservedTreatmentDateUnavailable')}{' '}
                  <Button type="button" variant="outline" size="sm" onClick={reservedDate.refetch}>
                    {t('subjects.reservedTreatmentDateRetry')}
                  </Button>
                </>
              )}
            </p>
          )}
        {subjectSnapshot !== null ? (
          <>
            <p>
              {t('subjects.provisionalIdentifierLabel')}: {subjectSnapshot.provisionalIdentifier}
            </p>
            {subjectSnapshot.definitiveIdentifier !== null && (
              <p>
                {t('subjects.definitiveIdentifierLabel')}: {subjectSnapshot.definitiveIdentifier}
              </p>
            )}
          </>
        ) : (
          <p className="text-muted-foreground">{t('subjects.identifiersUnknownHint')}</p>
        )}
      </div>

      {schedule.lifecycleState === 'CANCELLED' && (
        <p className="text-sm">{t('subjects.cancelledMessage')}</p>
      )}

      {schedule.lifecycleState === 'PROVISIONAL' && (
        <div className="space-y-4">
          <ContactLogSection studyId={studyId} subjectId={subjectId} />
          <TreatmentDateReservationSection
            studyId={studyId}
            subjectId={subjectId}
            onReserved={(subject) => {
              setSubjectSnapshot(subject);
              afterSubjectWrite();
            }}
          />
          <TreatmentExecutionConfirmationSection
            studyId={studyId}
            subjectId={subjectId}
            onConfirmed={(subject) => {
              setSubjectSnapshot(subject);
              afterSubjectWrite();
              refreshSchedule({ showLoading: false });
            }}
          />
          <Button
            type="button"
            variant="destructive"
            disabled={pending}
            onClick={() => void run(() => onDrop())}
          >
            <Trash2 className="size-4" aria-hidden="true" />
            {t('subjects.dropButton')}
          </Button>
          {dropApiError && <FormFeedback tone="error">{dropApiError}</FormFeedback>}
        </div>
      )}

      {(schedule.lifecycleState === 'PROVISIONAL' || schedule.lifecycleState === 'ENROLLED') && (
        <TreatmentDateRescheduleSection
          studyId={studyId}
          subjectId={subjectId}
          lifecycleState={schedule.lifecycleState}
          onRescheduled={(subject) => {
            setSubjectSnapshot(subject);
            afterSubjectWrite();
            refreshSchedule({ showLoading: false });
          }}
          onConflict={() => refreshSchedule({ showLoading: false })}
        />
      )}

      {schedule.lifecycleState === 'ENROLLED' && schedule.scheduleAvailable && (
        <>
          <Link
            to={`/clinical-study/studies/${studyId}/subjects/${subjectId}/participant-calendar`}
            className="text-sm underline"
          >
            {t('subjects.participantCalendarLink')}
          </Link>
          <ScheduleSection
            studyId={studyId}
            subjectId={subjectId}
            timepoints={schedule.timepoints}
            onVisitUpdated={handleVisitUpdated}
          />
        </>
      )}

      <section className="space-y-2">
        <h3 className="card-label">{t('subjects.reservedCalendarHeading')}</h3>
        <StudyCalendarAgenda
          studyId={studyId}
          subjectId={subjectId}
          subjectList={subjectList}
          canReadSchedules={canReadSchedules}
          canReadPool={canReadPool}
          // The panel lives inside StudySubjectsSection, which already reports a failed list.
          reportListError={false}
        />
      </section>
    </div>
  );
}
